import { useState, useEffect, useMemo } from 'react';
import lunr from 'lunr';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

import { ContractAuditData } from '@/graphql/contracts/_generated_/ContractAudit.fragment';
import { User } from '@/hooks/identity/responseModels/user';
import { DATE_TIME_PATTERN, DEFAULT_DATE_PATTERN, FULL_DATE_PATTERN } from '@/config/common.config';

interface UseHistorySearchProps {
  auditHistoryData: ContractAuditData[];
  isLoadingSearchUsers: boolean;
  users: User[];
}

interface UseHistorySearchReturn {
  searchTerm: string;
  filteredHistoryData: ContractAuditData[];
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dateFilter: { startDate: Date | null | undefined; endDate: Date | null | undefined };
  handleDateFilterChange: (startDate: Date | null | undefined, endDate: Date | null | undefined) => void;
}

export const useHistorySearch = ({
  auditHistoryData,
  isLoadingSearchUsers,
  users,
}: UseHistorySearchProps): UseHistorySearchReturn => {
  const [inputValue, setInputValue] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchIndex, setSearchIndex] = useState<lunr.Index | null>(null);
  const [dateFilter, setDateFilter] = useState<{
    startDate: Date | null | undefined;
    endDate: Date | null | undefined
  }>({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    if (auditHistoryData.length > 0 && !isLoadingSearchUsers) {
      lunr.tokenizer.separator = /\s+/;

      const index = lunr(function (this: lunr.Builder) {
        this.ref('changeSetID');
        this.field('header');
        this.field('staticDataValues');
        this.field('changeLabels');
        this.field('dateTimeValues');
        this.field('userNames');

        this.pipeline.remove(lunr.stemmer);
        this.searchPipeline.remove(lunr.stemmer);


        auditHistoryData.forEach((item) => {
          const staticDataValues = item.staticData
            ?.map(data => data.value)
            .filter(value => value != null && value.trim() !== '')
            .join(' ') || '';

          const changeLabels = item.changes
            ?.map(change => change.name)
            .filter(name => name != null && name.trim() !== '')
            .join(' ') || '';

          const dateTimeValues = item.timeStamp ? (() => {
            try {
              const date = dayjs(item.timeStamp);
              if (!date.isValid()) return '';

              const formats = [
                date.format(DATE_TIME_PATTERN),
                date.format(DEFAULT_DATE_PATTERN), 
                date.format(FULL_DATE_PATTERN), 
                date.format('DD MMM'),
                date.format('MMM YYYY'),
                date.format('YYYY'),
                date.format('MMM'),
                date.format('YYYY/MM/DD'),
                date.format('YYYY-MM-DD'),
                date.format('DD'),
                date.format('MM'),
              ];

              return formats.filter(f => f && f.trim() !== '').join(' ');
            } catch (error) {
              return '';
            }
          })() : '';

          let userNames = '';

          if (item.eventSourceType === 'SYSTEM') {
            userNames = 'System';
          } else if (item.eventSourceId) {
            const user = users.find(u => u.Id === item.eventSourceId);
            if (user) {
              const firstName = user.FirstName?.trim() || '';
              const lastName = user.LastName?.trim() || '';
              const fullName = `${firstName} ${lastName}`.trim();

              const nameParts = [firstName, lastName, fullName].filter(part => part && part.trim() !== '');
              userNames = nameParts.join(' ');
            }
          }

          const indexData = {
            changeSetID: item.changeSetID,
            header: item.header || '',
            staticDataValues,
            changeLabels,
            dateTimeValues,
            userNames
          };

          this.add(indexData);
        });
      });

      setSearchIndex(index);
    }
  }, [auditHistoryData, isLoadingSearchUsers, users]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    const delayedSearch = debounce(() => setSearchTerm(value), 300);
    delayedSearch();
  };

  const handleDateFilterChange = (startDate: Date | null | undefined, endDate: Date | null | undefined) => {
    setDateFilter({ startDate, endDate });
  };

  const filteredHistoryData = useMemo(() => {
    let data = auditHistoryData;

    if (searchTerm.trim() && searchIndex) {
      try {
        const term = searchTerm.trim().toLowerCase();
        const escaped_term = term.replace(/[+\-&|!^"~*?:\\\/]/g, '\\$&');
        const searchResults = searchIndex.search(`*${escaped_term}*`);
        const resultIds = new Set(searchResults.map((result: any) => result.ref));
        data = data.filter(item => resultIds.has(item.changeSetID));
      } catch (error) {
        data = [];
      }
    }

    if (dateFilter.startDate) {
      data = data.filter(item => {
        if (!item.timeStamp) return false;

        try {
          const itemDate = dayjs(item.timeStamp);
          if (!itemDate.isValid()) return false;

          const itemDateOnly = itemDate.startOf('day');
          const selectedDate = dayjs(dateFilter.startDate).startOf('day');

          return itemDateOnly.isSame(selectedDate, 'day');
        } catch (error) {
          return false;
        }
      });
    }

    return data;
  }, [searchTerm, searchIndex, auditHistoryData, dateFilter]);

  return {
    searchTerm: inputValue,
    filteredHistoryData,
    handleSearchChange,
    dateFilter,
    handleDateFilterChange,
  };
};
